import React, { PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { useConfig } from "../lib/config";
import Keycloak, { KeycloakInitOptions } from "keycloak-js";
import {
  ReactKeycloakProvider as BaseProvider,
  useKeycloak as useBaseKeycloak,
} from "@react-keycloak/web";
import { UserProfileProvider } from "./user-profile-provider";
// @ts-ignore
import KeycloakAuthorization from "keycloak-js/authz";

export let KeycloakSingleton: Keycloak | undefined = undefined;
export let KeycloakRPTSingleton: { rpt: string | undefined } = { rpt: undefined };
export interface KeycloakProviderProps {
  LoadingComponent: JSX.Element;
}

const KeycloakAuthzContext = React.createContext<KeycloakAuthorization | undefined>(
  undefined
);

export function KeycloakProvider(props: PropsWithChildren<KeycloakProviderProps>) {
  const config = useConfig();
  const [authz, setAuthz] = useState<KeycloakAuthorization | undefined>();
  const keycloak = useMemo(
    () =>
      new Keycloak({
        realm: config.keycloakRealm,
        url: config.keycloakUrl,
        clientId: config.keycloakClientId,
      }),
    [config]
  );
  KeycloakSingleton = keycloak;

  const keycloakInitOptions: KeycloakInitOptions = {
    token: keycloak?.token,
    refreshToken: keycloak?.refreshToken,
    checkLoginIframe: false,
    silentCheckSsoRedirectUri: window.location.origin + "/v6/ng/silent-check-sso.html",
  };

  //InitializeKeycloak
  useEffect(() => {
    async function initKeycloakAuthz() {
      //Init authz
      const authz = new KeycloakAuthorization(keycloak);
      //We have to manually poll to check when this is ready.
      await new Promise(resolve => {
        const handle = setInterval(() => {
          if (authz.config) {
            clearInterval(handle);
            //@ts-ignore
            resolve();
          }
        }, 20);
      });
      setAuthz(authz);
    }

    initKeycloakAuthz().catch(err => console.error("Error initializing keycloak authz", err));
  }, [keycloak]);

  return (
    <BaseProvider
      authClient={keycloak}
      initOptions={keycloakInitOptions}
      autoRefreshToken
      LoadingComponent={props.LoadingComponent}>
      <>
        {authz && (
          <KeycloakAuthzContext.Provider value={authz}>
            <UserProfileProvider>
              {props.children}
            </UserProfileProvider>
          </KeycloakAuthzContext.Provider>
        )}
        {!authz && props.LoadingComponent}
      </>
    </BaseProvider>
  );
}

export function useKeycloak(): Keycloak {
  const { keycloak } = useBaseKeycloak();
  return keycloak;
}

export function useKeycloakAuthz(): KeycloakAuthorization {
  const data = useContext(KeycloakAuthzContext);
  return data!!;
}
